import { useCallback, useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import { ConfirmationModal } from '@/components/modals';

import { BRANDING_CONFIG, MAX_FILE_SIZE } from '../../constants';
import { useBranding } from '../../hooks';
import { BrandingType } from '../../types';
import { formatErrorMessage } from '../../utils';

import { ImageSection } from './ImageSection';

const ImageSkeleton = () => (
  <Stack spacing={2} sx={{ width: '200px' }}>
    <Skeleton variant="text" width={'65%'} height={32} />
    <Skeleton
      variant="rounded"
      width={200}
      height={200}
      sx={{ borderRadius: '10px' }}
    />
    <Stack direction={'row'} spacing={1}>
      <Skeleton variant="rounded" width={200} height={32} />
      <Skeleton variant="rounded" width={200} height={32} />
    </Stack>
  </Stack>
);

export const BrandingInformation = () => {
  const {
    branding,
    isLoading,
    isError,
    error,
    saveBranding,
    isSaving,
    deleteBranding,
    isDeleting,
    refetch,
  } = useBranding();

  const [uploadErrors, setUploadErrors] = useState<
    Record<BrandingType, string | undefined>
  >({
    logo: undefined,
    letterhead: undefined,
    follower: undefined,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageTypeToDelete, setImageTypeToDelete] =
    useState<BrandingType | null>(null);

  const validateFile = useCallback(
    (file: File, type: BrandingType): string | undefined => {
      if (file.size > MAX_FILE_SIZE) {
        return 'File size must be less than 5MB';
      }

      const extension = file.name.split('.').pop()?.toLowerCase();
      const config = BRANDING_CONFIG[type];

      if (!extension || !config.allowedExtensions.includes(extension)) {
        const allowedExtensions = config.allowedExtensions
          .join(', ')
          .toUpperCase();
        return `${config.label} must be a ${allowedExtensions} file`;
      }

      return undefined;
    },
    [],
  );

  const clearError = useCallback((type: BrandingType) => {
    setUploadErrors((prev) => ({ ...prev, [type]: undefined }));
  }, []);

  const setError = useCallback((type: BrandingType, error: string) => {
    setUploadErrors((prev) => ({ ...prev, [type]: error }));
  }, []);

  const handleUpload = useCallback(
    (type: BrandingType) => (file: File) => {
      clearError(type);

      const validationError = validateFile(file, type);
      if (validationError) {
        setError(type, validationError);
        return;
      }

      const formData = new FormData();
      formData.append('image', file);
      formData.append('image_type', type);

      void saveBranding(formData, {
        onSuccess: () => {
          clearError(type);
        },
        onError: (error: unknown) => {
          if (error && typeof error === 'object' && 'response' in error) {
            const apiError = error as {
              response?: { data?: { message?: string } };
            };
            setError(type, formatErrorMessage(apiError));
          } else {
            setError(type, 'An unexpected error occurred');
          }
        },
      });
    },
    [clearError, validateFile, setError, saveBranding],
  );

  const handleDelete = useCallback(
    (type: BrandingType) => () => {
      if (!branding?.[type]) return;
      setImageTypeToDelete(type);
      setDialogOpen(true);
    },
    [branding],
  );

  const handleConfirmDelete = useCallback(() => {
    if (!imageTypeToDelete || !branding?.[imageTypeToDelete]) return;

    const imageId = branding[imageTypeToDelete].split('/').pop()?.split('.')[0];
    if (!imageId) return;

    void deleteBranding(
      { imageId, imageType: imageTypeToDelete },
      {
        onSuccess: () => {
          setDialogOpen(false);
          setImageTypeToDelete(null);
        },
        onError: (error: unknown) => {
          if (error && typeof error === 'object' && 'response' in error) {
            const apiError = error as {
              response?: { data?: { message?: string } };
            };
            setError(imageTypeToDelete, formatErrorMessage(apiError));
          } else {
            setError(imageTypeToDelete, 'An unexpected error occurred');
          }
        },
      },
    );
  }, [imageTypeToDelete, branding, deleteBranding, setError]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setImageTypeToDelete(null);
  }, []);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (isError) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Alert
          severity="error"
          sx={{ width: '100%' }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRetry}
              startIcon={<RefreshIcon />}
            >
              Try Again
            </Button>
          }
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            Error loading branding images
          </Typography>
          <Typography variant="body2" color="inherit">
            {error?.message || 'An unexpected error occurred'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={3} padding={3}>
        {(Object.keys(BRANDING_CONFIG) as BrandingType[]).map((type) => {
          const config = BRANDING_CONFIG[type];
          return (
            <Grid item xs={12} md={4} key={type}>
              {isLoading ? (
                <ImageSkeleton />
              ) : (
                <ImageSection
                  title={config.title}
                  tooltip={config.tooltip}
                  imageUrl={branding?.[type]}
                  onUpload={handleUpload(type)}
                  onDelete={handleDelete(type)}
                  isLoading={isSaving || isDeleting}
                  error={uploadErrors[type]}
                  accept={config.accept}
                />
              )}
            </Grid>
          );
        })}
      </Grid>

      <ConfirmationModal
        open={dialogOpen}
        isPending={isDeleting}
        onClose={handleCloseDialog}
        title={`Delete ${imageTypeToDelete ? BRANDING_CONFIG[imageTypeToDelete].label : 'Image'}`}
        content={`Are you sure you want to delete this ${imageTypeToDelete ? BRANDING_CONFIG[imageTypeToDelete].label.toLowerCase() : 'image'}? This action cannot be undone.`}
        cancelActionTitle="Cancel"
        confirmActionTitle="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDialog}
      />
    </>
  );
};
